import { blupayApi, getApiError } from "../api/client";
import { tokenService } from "../api/token.service";

const BASE = "/me/chat/conversations";

async function unwrap(request) {
  try {
    const { data } = await request;
    return data;
  } catch (error) {
    throw getApiError(error);
  }
}

/**
 * Parses server-sent events from a stream of text chunks.
 * Calls onEvent(name, data) for every complete frame.
 */
export async function readEventStream(chunks, onEvent) {
  let buffer = "";

  function flush(frame) {
    let name = "message";
    const data = [];

    frame.split("\n").forEach((line) => {
      if (line.startsWith("event:")) name = line.slice(6).trim();
      if (line.startsWith("data:")) data.push(line.slice(5).trim());
    });

    if (data.length) onEvent(name, JSON.parse(data.join("\n")));
  }

  for await (const chunk of chunks) {
    buffer += chunk.replace(/\r\n/g, "\n");
    let boundary = buffer.indexOf("\n\n");

    while (boundary !== -1) {
      flush(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf("\n\n");
    }
  }

  if (buffer.trim()) flush(buffer);
}

async function* decodeBody(body) {
  const reader = body.getReader();
  const decoder = new TextDecoder();

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      yield decoder.decode(value, { stream: true });
    }
  } finally {
    reader.releaseLock();
  }
}

function postStream(url, body, signal) {
  const token = tokenService.getAccessToken();

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
}

async function streamError(response) {
  let data = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  return getApiError({ response: { status: response.status, data } });
}

export const chatService = {
  async listConversations({ archived = false, limit = 30 } = {}) {
    return unwrap(blupayApi.get(BASE, { params: { archived, limit } }));
  },

  async createConversation(payload = {}) {
    return unwrap(blupayApi.post(BASE, payload));
  },

  async updateConversation(conversationId, payload) {
    return unwrap(blupayApi.patch(`${BASE}/${conversationId}`, payload));
  },

  async listMessages(conversationId, { before = null, limit = 30 } = {}) {
    const params = before ? { before, limit } : { limit };
    return unwrap(blupayApi.get(`${BASE}/${conversationId}/messages`, { params }));
  },

  async recordAssistantMessages(conversationId, messages) {
    return unwrap(
      blupayApi.post(`${BASE}/${conversationId}/assistant-messages`, { messages })
    );
  },

  /**
   * Posts a message and reports each server-sent event through onEvent.
   * A 401 refreshes the session once through the axios client, then retries.
   */
  async streamMessage(conversationId, { content, step }, { onEvent, signal } = {}) {
    const url = `${blupayApi.defaults.baseURL}${BASE}/${conversationId}/messages`;
    const body = { content, step };

    let response;

    try {
      response = await postStream(url, body, signal);

      if (response.status === 401) {
        await blupayApi.get(BASE, { params: { limit: 1 } });
        response = await postStream(url, body, signal);
      }
    } catch (error) {
      throw getApiError(error);
    }

    if (!response.ok) {
      throw await streamError(response);
    }

    await readEventStream(decodeBody(response.body), onEvent);
  },
};
